'use client';
import { createContext, useContext, useMemo, useEffect, type ReactNode } from 'react';
import type { IThemeStorage } from '../interfaces/IThemeStorage';
import type { IThemeDOM } from '../interfaces/IThemeDOM';
import { LocalStorageThemeStorage } from '../storage/LocalStorageThemeStorage';
import { BrowserThemeDOM } from '../dom/BrowserThemeDOM';
import { ThemeService } from '../core/ThemeService';

interface ThemeContextValue {
	service: ThemeService;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
	children: ReactNode;
	storage?: IThemeStorage;
	dom?: IThemeDOM;
}

export function ThemeProvider({ children, storage, dom }: Readonly<ThemeProviderProps>) {
	const service = useMemo(() => {
		const storageInstance = storage ?? new LocalStorageThemeStorage();
		const domInstance = dom ?? new BrowserThemeDOM();
		return new ThemeService(storageInstance, domInstance);
	}, [storage, dom]);

	useEffect(() => {
		const initialTheme = service.getTheme();
		const initialMode = service.getMode();

		service.setTheme(initialTheme);
		service.setMode(initialMode);
	}, [service]);

	const value = useMemo(() => ({ service }), [service]);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeService(): ThemeService {
	const context = useContext(ThemeContext);

	if (!context) {
		throw new Error('useThemeService must be used within ThemeProvider');
	}

	return context.service;
}
